import { createSlice } from '@reduxjs/toolkit';

import type { JsonFailure } from '@skynet/data-ws-core';
import { parseResponse, registerErrorMessage } from '@skynet/data-ws-core';

import type { ExchangeSliceFactoryProps, JsonExchangesListSuccess } from './types';
import { ExchangeCommands } from './types';

registerErrorMessage(ExchangeCommands.Exchanges, {
  title: 'Exchanges Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

/**
 * Creates a channel-agnostic exchanges list slice. Each domain instance
 * (e.g. MM, ES) wires its own WebSocket `messages` action creator and mount
 * point in the app.
 */
export function initExchangeSlice({
  prefix = 'shared',
  sliceName = 'exchanges',
  commands = ExchangeCommands,
  messages,
  initialState,
}: ExchangeSliceFactoryProps) {
  if (!initialState) {
    throw new Error('Invalid initial state');
  }

  const allowedCommands = Object.values(commands);

  const exchangeSlice = createSlice({
    name: `${prefix}/${sliceName}`,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      // on WS message

      builder.addCase(messages, (state, action) => {
        const { json, skipProcessing, error, errorCommand } = parseResponse(
          action.payload,
          allowedCommands,
        );

        if (skipProcessing || !json) return;

        console.debug(
          `${prefix}/${sliceName}/processing ${messages.toString()}`,
          action,
          allowedCommands,
        );

        const { command } = json;

        if (error) {
          state.error = error;

          switch (errorCommand) {
            // case BalanceCommand.__ACTION_NAME__:
            //   state.res__ACTION_NAME__ = { status: 'error' };
            //   break;
            case undefined:
            default:
          }

          return;
        }

        if (command === ExchangeCommands.Exchanges) {
          const { list }: JsonExchangesListSuccess = json;

          state.list = (list ?? []).map((exchange) => ({
            id: exchange.ID,
            name: exchange.Name,
            ticker: exchange.Ticker,
          }));
          state.requestStatus = 'success';
          return;
        }
      });
    },
  });

  return exchangeSlice;
}
